/**
 * Las piezas de gráfico, compartidas.
 *
 * Vivían locales en `DashboardPage`, así que el Ejecutivo —la pantalla que un gerente abre
 * para decidir— no tenía **ni un gráfico**: sus "barras" eran `div`s con `width` en
 * porcentaje, sin eje, sin escala y sin tooltip. Recharts ya era dependencia y estaba bien
 * usado a una pantalla de distancia.
 *
 * Los colores salen de los tokens `--chart-1..5` de `index.css`, que existían desde el
 * scaffold de shadcn y no usaba nadie: los charts tenían los hex escritos adentro, así que
 * cambiar la paleta era editar cuatro archivos.
 */
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';

/** La paleta categórica. Cinco tokens; a partir del sexto se repite el ciclo. */
export const SERIES = [
  'hsl(var(--chart-1))',
  'hsl(var(--chart-2))',
  'hsl(var(--chart-3))',
  'hsl(var(--chart-4))',
  'hsl(var(--chart-5))',
];

export const colorDeSerie = (i) => SERIES[i % SERIES.length];

/**
 * El semáforo del SLA. No es categórico: cada color **significa** algo, así que no puede
 * salir del ciclo de series ni cambiar de posición.
 */
export const COLOR_SEMAFORO = {
  ok: '#00D15C',
  risk: '#FFB800',
  breach: '#FF2A00',
  // Gris a propósito: lo que no tiene reloj no es ni bueno ni malo, y pintarlo de verde
  // —que es lo que hacía el tablero viejo— muestra como cumplido lo que no se está midiendo.
  sin_reloj: '#A1A1AA',
};

export const ETIQUETA_SEMAFORO = {
  ok: 'En plazo',
  risk: 'En riesgo',
  breach: 'Vencido',
  sin_reloj: 'Sin reloj',
};

/**
 * Un KPI.
 *
 * **`ayuda` llegó el 2026-10-05, y antes no había forma de poner una.** Esta tarjeta sólo aceptaba
 * `sub` —una línea gris— así que ningún indicador del tablero ejecutivo podía explicarse: 22 no
 * tenían absolutamente nada. Se le pasa un `<ComoSeCalcula>` armado con el universo que vino con el
 * número y con los parámetros de la cápsula; ver ese componente.
 */
export function KpiCard({
  label, value, icon: Icon, color = 'text-[#09090B]', sub, loading, danger, ayuda,
}) {
  const tono = danger ? 'text-brand-danger' : color;
  return (
    <Card
      className="border border-brand-border bg-white rounded-sm hover:shadow-md transition-shadow duration-200"
      data-testid={`kpi-${String(label).toLowerCase().replace(/\s/g, '-')}`}
    >
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-2">
          <div className="space-y-1 min-w-0">
            <p className="text-xs font-semibold uppercase tracking-[0.1em] text-zinc-500">{label}</p>
            {loading ? (
              <Skeleton className="h-9 w-16" />
            ) : (
              <p className={`text-3xl font-heading font-bold tracking-tight ${tono}`}>{value}</p>
            )}
            {sub && !loading && <p className="text-xs text-zinc-400 mt-1">{sub}</p>}
            {ayuda && !loading && ayuda}
          </div>
          {Icon && (
            <div className="p-2.5 rounded-sm bg-zinc-50 shrink-0">
              <Icon className={`w-5 h-5 ${tono}`} />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export function ChartCard({ title, subtitle, children, className = '', ayuda, ...props }) {
  // `...props` se propaga a propósito: sin eso los `data-testid` que le pasaban en
  // DashboardPage se perdían y nunca llegaban al DOM.
  return (
    <Card className={`border border-brand-border bg-white rounded-sm ${className}`} {...props}>
      <CardHeader className="pb-2 pt-4 px-5">
        <CardTitle className="text-xs font-semibold uppercase tracking-[0.1em] text-zinc-500">
          {title}
        </CardTitle>
        {subtitle && <p className="text-[10px] text-zinc-400 font-normal normal-case">{subtitle}</p>}
        {ayuda}
      </CardHeader>
      <CardContent className="px-5 pb-5">{children}</CardContent>
    </Card>
  );
}

export function CustomTooltip({ active, payload, label, sufijo = '' }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#09090B] text-white px-3 py-2 rounded-sm text-xs shadow-lg border border-zinc-700">
      {label != null && <p className="font-semibold mb-1">{label}</p>}
      {payload.map((p, i) => (
        <p key={i} className="text-zinc-300">
          <span style={{ color: p.color || p.fill }}>{p.name || p.dataKey}: </span>
          <span className="font-bold text-white">{p.value}{sufijo}</span>
        </p>
      ))}
    </div>
  );
}

/** Una barra apilada del semáforo. Lee igual que la de la pantalla de categorías. */
/**
 * El semáforo como una barra, con su universo en el `title`.
 *
 * **`universo` llegó el 2026-10-05, al unificar un duplicado.** `CategoriasPage` tenía su propia
 * copia de este componente, idéntica salvo en una cosa que hacía mejor: su `title` decía *«N
 * abiertos de M»* en vez de *«N unidades»*, o sea **declaraba sobre qué se midió**. Al juntarlos se
 * quedó la versión que dice más; `vacio` hace lo mismo con el caso sin datos.
 */
export function BarraDeSemaforo({ sla, alto = 'h-2', universo = '', vacio = 'Nada que medir' }) {
  const { ok = 0, risk = 0, breach = 0, sin_reloj: sinReloj = 0 } = sla || {};
  const total = ok + risk + breach + sinReloj;
  if (!total) return <div className={`${alto} rounded-sm bg-zinc-100`} title={vacio} />;
  const pct = (n) => `${(n / total) * 100}%`;
  return (
    <div
      className={`${alto} rounded-sm overflow-hidden flex bg-zinc-100`}
      title={universo || `${total} unidades`}
    >
      {breach > 0 && <div style={{ width: pct(breach), backgroundColor: COLOR_SEMAFORO.breach }} title={`${breach} vencidos`} />}
      {risk > 0 && <div style={{ width: pct(risk), backgroundColor: COLOR_SEMAFORO.risk }} title={`${risk} en riesgo`} />}
      {ok > 0 && <div style={{ width: pct(ok), backgroundColor: COLOR_SEMAFORO.ok }} title={`${ok} en plazo`} />}
      {sinReloj > 0 && <div style={{ width: pct(sinReloj), backgroundColor: COLOR_SEMAFORO.sin_reloj }} title={`${sinReloj} sin reloj`} />}
    </div>
  );
}

export { default as ComoSeCalcula } from './ComoSeCalcula';
